import { ContractId, AccountId } from '@hashgraph/sdk';
import { ContractFunctionParameterBuilder } from './wallets/contractFunctionParameterBuilder';
import { WalletInterface } from './wallets/walletInterface';
import { getContractAddress } from '../config/contracts';

export class ContractService {
  constructor(private walletInterface: WalletInterface) {}

  // ProfitableSaucerSwap contract functions
  async swapExactHBARForTokens(
    tokenOut: string,
    fee: number,
    amountOutMinimum: string,
    deadline: number,
    hbarAmount: number
  ) {
    const contractId = ContractId.fromString(getContractAddress('ProfitableSaucerSwap'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'tokenOut', value: tokenOut })
      .addParam({ type: 'uint24', name: 'fee', value: fee })
      .addParam({ type: 'uint256', name: 'amountOutMinimum', value: amountOutMinimum })
      .addParam({ type: 'uint256', name: 'deadline', value: deadline });

    // Add payable amount for HBAR swap
    (functionParameters as any).payableAmount = hbarAmount * 100000000; // Convert HBAR to tinybars

    return this.walletInterface.executeContractFunction(
      contractId,
      'swapExactHBARForTokens',
      functionParameters,
      300000
    );
  }

  async swapExactTokensForHBAR(
    tokenIn: string,
    fee: number,
    amountIn: string,
    amountOutMinimum: string,
    deadline: number
  ) {
    const contractId = ContractId.fromString(getContractAddress('ProfitableSaucerSwap'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'tokenIn', value: tokenIn })
      .addParam({ type: 'uint24', name: 'fee', value: fee })
      .addParam({ type: 'uint256', name: 'amountIn', value: amountIn })
      .addParam({ type: 'uint256', name: 'amountOutMinimum', value: amountOutMinimum })
      .addParam({ type: 'uint256', name: 'deadline', value: deadline });

    return this.walletInterface.executeContractFunction(
      contractId,
      'swapExactTokensForHBAR',
      functionParameters,
      300000
    );
  }

  async swapExactTokensForTokens(
    tokenIn: string,
    tokenOut: string,
    fee: number,
    amountIn: string,
    amountOutMinimum: string,
    deadline: number
  ) {
    const contractId = ContractId.fromString(getContractAddress('ProfitableSaucerSwap'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'tokenIn', value: tokenIn })
      .addParam({ type: 'address', name: 'tokenOut', value: tokenOut })
      .addParam({ type: 'uint24', name: 'fee', value: fee })
      .addParam({ type: 'uint256', name: 'amountIn', value: amountIn })
      .addParam({ type: 'uint256', name: 'amountOutMinimum', value: amountOutMinimum })
      .addParam({ type: 'uint256', name: 'deadline', value: deadline });

    return this.walletInterface.executeContractFunction(
      contractId,
      'swapExactTokensForTokens',
      functionParameters,
      300000
    );
  }

  async getUserStats(userAddress: string) {
    const contractId = ContractId.fromString(getContractAddress('ProfitableSaucerSwap'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'userAddress', value: userAddress });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getUserStats',
      functionParameters,
      100000
    );
  }

  async getUserSwapHistoryLength(userAddress: string) {
    const contractId = ContractId.fromString(getContractAddress('ProfitableSaucerSwap'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'userAddress', value: userAddress });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getUserSwapHistoryLength',
      functionParameters,
      100000
    );
  }

  // SwapLottery contract functions
  async getCurrentLottery() {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder();

    return this.walletInterface.executeContractFunction(
      contractId,
      'getContractStats',
      functionParameters,
      100000
    );
  }

  async getLotteryInfo(lotteryId: number) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'uint256', name: 'lotteryId', value: lotteryId.toString() });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getLottery',
      functionParameters,
      100000
    );
  }

  async getLotteryParticipants(lotteryId: number) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'uint256', name: 'lotteryId', value: lotteryId.toString() });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getLotteryParticipants',
      functionParameters,
      100000
    );
  }

  async getUserTickets(lotteryId: number, userAddress: string) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'uint256', name: 'lotteryId', value: lotteryId.toString() })
      .addParam({ type: 'address', name: 'userAddress', value: userAddress });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getUserTickets',
      functionParameters,
      100000
    );
  }

  async claimPrize(lotteryId: number) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'uint256', name: 'lotteryId', value: lotteryId.toString() });

    return this.walletInterface.executeContractFunction(
      contractId,
      'claimPrize',
      functionParameters,
      200000
    );
  }

  async selectWinner(lotteryId: number, winningNumber: number) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'uint256', name: 'lotteryId', value: lotteryId.toString() })
      .addParam({ type: 'uint256', name: 'winningNumber', value: winningNumber.toString() });

    return this.walletInterface.executeContractFunction(
      contractId,
      'selectWinner',
      functionParameters,
      300000
    );
  }

  async getUserLotteryStats(userAddress: string) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder()
      .addParam({ type: 'address', name: 'userAddress', value: userAddress });

    return this.walletInterface.executeContractFunction(
      contractId,
      'getUserStats',
      functionParameters,
      100000
    );
  }

  async addToPrizePool(amount: number) {
    const contractId = ContractId.fromString(getContractAddress('SwapLottery'));
    const functionParameters = new ContractFunctionParameterBuilder();

    return this.walletInterface.executeContractFunction(
      contractId,
      'addToPrizePool',
      functionParameters,
      200000
    );
  }
}